/**
 * Нативный захват экрана — то, чего в браузере не сделать.
 *
 * Замер на живой игре объяснил, зачем это нужно. Захват заказан на 60 кадров,
 * кодировщик тратит на кадр 5.5 мс (то есть способен на 180), полоса свободна,
 * ничего не ограничивает — а источник отдаёт 15 кадров в секунду. Кадры не
 * доходят до кодировщика, потому что браузер их не снимает, и настройками это
 * не лечится: менять там нечего.
 *
 * Здесь тот же путь идёт мимо браузера: снимает Desktop Duplication, кодирует
 * видеокарта, готовый поток уходит в LiveKit по WHIP. На том же железе это
 * дало 57 кадров вместо 15. Заодно пропадает жёлтая рамка вокруг захваченного
 * экрана: её рисует Chromium вокруг отданного через `getDisplayMedia`, и
 * страница выключить её не может.
 *
 * Модуль весь целиком — про наличие. Тех же кнопок в вебе нет, и ветка выбора
 * должна быть ровно одна, здесь.
 *
 * Каждый шаг здесь пишется в журнал, и это не украшение. Раньше отказы тут
 * глотались молча: `hasNativeCapture` при любой беде отвечала «захвата нет»,
 * списки возвращали пустоту, и снаружи «оболочка старая» выглядело так же,
 * как «команда упала». Показ у человека не запускался, а сказать о нём было
 * нечего — с этого журнал и начался.
 */

import { fail, mask, note } from "@shared/lib/journal";

/**
 * Запущены ли мы внутри оболочки.
 *
 * Признак — `window.airelShell`, который кладёт preload Electron. В вебе его
 * нет, и командам захвата отвечать некому.
 */
function inShell(): boolean {
  return typeof window !== "undefined" && !!window.airelShell;
}

let cached: boolean | null = null;

/**
 * Есть ли нативный захват.
 *
 * Мало быть в оболочке: захват собирается только под Windows — там Desktop
 * Duplication и Media Foundation, — и приложение под другую систему его не
 * несёт. Спрашиваем саму оболочку, а не гадаем по строке клиента.
 *
 * Ответ не меняется за время работы, поэтому запоминается.
 */
export async function hasNativeCapture(): Promise<boolean> {
  if (!inShell()) return false;
  if (cached !== null) return cached;

  try {
    cached = (await window.airelShell?.capture.has()) ?? false;
    note("захват", "оболочка отвечает про захват", { есть: cached });
  } catch (error) {
    // Старая сборка оболочки без этой команды. Считаем, что захвата нет:
    // отсутствие лучше, чем падение при попытке показать экран.
    fail("захват", "оболочка не ответила про захват — считаем, что его нет", error);
    cached = false;
  }

  return cached;
}

/** Экран, который можно показать. */
export interface NativeScreen {
  /** Номер для захвата. Совпадает с порядком у видеокарты. */
  index: number;
  name: string;
  width: number;
  height: number;
  /** Основной монитор — тот, где панель задач. */
  primary: boolean;
  /** Снимок как `data:`-адрес. Пусто, если снять не вышло. */
  thumbnail: string;
}

/**
 * Что можно показать прямо сейчас.
 *
 * Спрашивается заново на каждое обновление: монитор могли отключить, а на
 * оставшемся — открыть другое окно. Ради второго превью и нужны — экраны
 * различают по картинке, а не по имени.
 */
export async function listScreens(): Promise<NativeScreen[]> {
  if (!inShell()) return [];

  try {
    const screens = ((await window.airelShell?.capture.listScreens()) ?? []) as NativeScreen[];
    note("захват", "экраны перечислены", { сколько: screens.length });
    return screens;
  } catch (error) {
    fail("захват", "экраны не перечислились", error);
    return [];
  }
}

/** Окно, которое можно показать. */
export interface NativeWindow {
  /** Номер окна. Уходит в захват как есть. */
  hwnd: number;
  title: string;
  /** Чьё это окно — имя программы. Заголовки повторяются, имена реже. */
  app: string;
  width: number;
  height: number;
  thumbnail: string;
}

/**
 * Окна, которые можно показать.
 *
 * Отдельно от экранов: превью снимаются по одному, и список заметно дороже —
 * спрашивать его, пока открыта вкладка с экранами, незачем.
 */
export async function listWindows(): Promise<NativeWindow[]> {
  if (!inShell()) return [];

  try {
    const windows = ((await window.airelShell?.capture.listWindows()) ?? []) as NativeWindow[];
    note("захват", "окна перечислены", { сколько: windows.length });
    return windows;
  } catch (error) {
    fail("захват", "окна не перечислились", error);
    return [];
  }
}

/**
 * Приложения, которые можно показать.
 *
 * Список тот же, что у окон, — разница в превью. Здесь они сняты просьбой к
 * самим окнам, то есть так же, как приложение потом покажут: без того, что
 * лежит поверх. Ради этого различия и заведена отдельная вкладка, и оно
 * должно быть видно на плитке до выбора, а не после.
 */
export async function listApps(): Promise<NativeWindow[]> {
  if (!inShell()) return [];

  try {
    const apps = ((await window.airelShell?.capture.listApps()) ?? []) as NativeWindow[];
    note("захват", "приложения перечислены", { сколько: apps.length });
    return apps;
  } catch (error) {
    fail("захват", "приложения не перечислились", error);
    return [];
  }
}

export interface NativeCapture {
  /** Полный адрес WHIP с ключом — его выдаёт наш API. */
  url: string;
  fps: number;
  /**
   * Высота картинки: 1080, 720, 480. Ширина выводится по соотношению сторон
   * монитора — растянуть 16:10 в 16:9 значило бы исказить картинку.
   */
  height: number;
  /** Мегабиты в секунду. */
  mbps: number;
  /** Номер монитора; по умолчанию первый. */
  monitor?: number;
  /**
   * Номер окна. Задан — показывается окно, а монитор не при чём.
   *
   * Снимается оно всё равно вырезкой из монитора, на котором лежит: снимать
   * само окно нечем — Graphics Capture рисует жёлтую рамку, а текстура
   * диспетчера окон у игр и браузеров не обновляется вовсе.
   */
  window?: number;
  /**
   * Номер окна, которое покажем, попросив его нарисоваться самому.
   *
   * Отличие от `window` не в источнике номера, а в способе съёмки: там
   * вырезка из экрана вместе с чужими окнами поверх, здесь — только само
   * приложение.
   */
  app?: number;
  /**
   * Номер окна, у процесса которого взять звук.
   *
   * Именно у процесса: тогда в поток попадёт только звук игры, а голоса
   * собеседников — нет, они звучат из другого процесса.
   */
  sound?: number;
}

export async function startNativeCapture(options: NativeCapture): Promise<void> {
  note("захват", "прошу оболочку начать захват", {
    адрес: mask(options.url),
    кадров: options.fps,
    высота: options.height,
    мбит: options.mbps,
    монитор: options.monitor,
    окно: options.window,
    приложение: options.app,
    звук: options.sound,
  });

  try {
    // Одним объектом: доводов шесть, и два числа подряд легко перепутать
    // местами. Оболочка принимает их той же структурой.
    await window.airelShell?.capture.start({
      url: options.url,
      fps: options.fps,
      height: options.height,
      mbps: options.mbps,
      monitor: options.monitor ?? null,
      window: options.window ?? null,
      app: options.app ?? null,
      sound: options.sound ?? null,
    });

    note("захват", "оболочка приняла просьбу");
  } catch (error) {
    /*
     * Отказ пишется здесь и перебрасывается дальше: наверху из него
     * получится строка для человека, а причину целиком надо сохранить до
     * того, как её сократят до «Показать экран не вышло».
     */
    fail("захват", "оболочка отказала в захвате", error);
    throw error;
  }
}

/**
 * Числа, которые программа захвата сообщает о себе раз в секунду.
 *
 * Всё — за последнюю секунду, а не с начала показа: среднее за всё время
 * скрывает ровно то, что интересно, — что происходит сейчас.
 */
export interface CaptureStats {
  encoded: number;
  sent: number;
  /** Пропущено ради заказанной частоты. Это норма, а не потеря. */
  skipped: number;
  /** Выброшено, потому что отдача не поспевала. Вот это потеря. */
  dropped: number;
  /**
   * Потери по отчёту получателя, в процентах.
   *
   * Ноль без зрителя ничего не значит: отчёты приёма шлёт тот, кто смотрит,
   * и пока никто не подписан, их просто нет.
   */
  loss: number;
  /** Круговая задержка по тому же отчёту, миллисекунды. */
  rtt: number;
  /** Просьб переслать потерянное — всего за сеанс. */
  nacks: number;
  /** Просьб прислать ключевой кадр — всего за сеанс. */
  plis: number;
  /**
   * Сколько ключевых кадров в самом деле вышло из кодировщика.
   *
   * Не попыток: команду «дай ключевой кадр» кодировщик NVIDIA принимает и не
   * применяет, и счётчик попыток показывал бы полсотни там, где кадр один.
   */
  keys: number;
  /**
   * Шаг квантования. Ноль — кодировщик его не сообщает.
   *
   * Кодировщик NVIDIA не сообщает: свойства кадра перечислены, шага среди
   * них нет. Поле оставлено, потому что другие его кладут.
   */
  qp: number;
  /**
   * Бит на точку кадра — замена шагу квантования.
   *
   * Считается из того, что и так известно: сколько байт вышло и сколько в
   * кадре точек. Мера грубее шага, но направление показывает верно, и с ней
   * качество перестаёт обсуждаться на глаз.
   */
  bpp: number;
  /**
   * Насколько неровно идут кадры: среднее отклонение промежутка от ровного,
   * миллисекунды.
   *
   * Средней частоты для плавности мало. Шестьдесят кадров, из которых
   * половина пришла вплотную, а половина с двойной паузой, в среднем те же
   * шестьдесят — а смотрится рвано.
   */
  spread: number;
  /**
   * Самая долгая пауза между кадрами за секунду, миллисекунды.
   *
   * Ровность усредняет и прячет редкий провал: сто ровных кадров и один с
   * паузой в треть секунды дадут прекрасное среднее и заметный глазу рывок.
   * Микрофриз — это именно провал.
   */
  pause: number;
  /**
   * Кусков звука отдано за секунду. Пятьдесят — норма, по одному на двадцать
   * миллисекунд.
   *
   * Ноль означает, что звук не берётся вовсе, и это не то же самое, что
   * молчащая игра: молчание всё равно отдаётся кусками.
   */
  sound: number;
  /**
   * Просили ли звук вообще.
   *
   * Ноль кусков при непрошенном звуке и при прошенном — разные беды: первая
   * чинится галочкой, вторая в коде.
   */
  soundOn: boolean;
  /**
   * Что показываем: `screen`, `window` или `app`.
   *
   * По остальным числам источник неразличим, а разница видна человеку:
   * вырезка тянет в кадр чужие окна, приложение — нет. Звук возможен у обоих
   * оконных способов и невозможен у монитора: у него нет своего процесса.
   */
  shows: string;
  coder: string;
}

/**
 * Подписывается на отчёты программы захвата.
 *
 * Нужно потому, что у браузера своих чисел отправителя нет: нативный поток
 * он не кодирует, а только принимает. Без этого сторона отправителя в
 * показателях пуста.
 */
export async function onCaptureStats(
  handler: (stats: CaptureStats) => void,
): Promise<() => void> {
  if (!inShell()) return () => {};

  return window.airelShell?.capture.onStats((stats) => handler(stats as CaptureStats)) ?? (() => {});
}

/** Останавливает захват. Останавливать незапущенное — не ошибка. */
export async function stopNativeCapture(): Promise<void> {
  if (!inShell()) return;

  try {
    await window.airelShell?.capture.stop();
    note("захват", "остановка отправлена");
  } catch (error) {
    // Оболочка могла уже закрываться. Процесс она снимет сама при выходе.
    fail("захват", "остановку не приняли", error);
  }
}

export { hasAppSound, listSoundApps, startAppSound, type AppSound, type SoundApp } from "./sound";
